import { CreditScoreGraph } from "shared/app/CreditScoreGraph";
import { Card } from "shared/components/Card";
import { cx } from "shared/utils";
import { toMoney } from "utils/currency";

import { CriteriaBadgeIcon } from "shared/components/CriteriaBadgeIcon";
import { ApplicantSettings } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import { RecommendationMethod } from "types/landlord/verificationTemplate";

const Stat = ({
  label,
  value,
  selfReported,
  isCreditScore,
  isRentRatio,
  isPercentage,
  criteriaImportance,
  hasCriteria,
  criteriaSuccess
}: {
  isCreditScore?: boolean;
  isRentRatio?: boolean;
  isPercentage?: boolean;
  label: string;
  value?: number | null;
  selfReported?: boolean;
  criteriaImportance?: number | null;
  hasCriteria?: boolean;
  criteriaSuccess?: boolean;
}) => {
  return (
    <div
      className={cx(
        "px-6 h-full flex flex-col gap-2",
        selfReported ? "py-[30px]" : "py-6"
      )}
    >
      <h3
        className={
          selfReported
            ? "text-xs font-medium text-content-300"
            : "text-sm font-medium text-content-1000"
        }
      >
        {label}
      </h3>
      <p
        className={
          selfReported
            ? "text-base flex items-center gap-2 font-bold text-content-300"
            : "text-xl flex items-center gap-2 font-bold text-content-1000"
        }
      >
        {criteriaImportance == null ? null : (
          <CriteriaBadgeIcon
            hasCriteria={Boolean(hasCriteria)}
            success={Boolean(criteriaSuccess)}
          />
        )}

        {value === undefined || value === null
          ? "-"
          : isCreditScore
            ? value === 0
              ? "-"
              : value
            : isRentRatio
              ? value.toFixed(2) + "x"
              : isPercentage
                ? value.toFixed(2) + "%"
                : toMoney(value)}
      </p>
    </div>
  );
};

export const GroupStats = ({
  criteriaTypeOptions,
  totalVerifiedNet,
  creditScoreAvg,
  selfAttestedIncomeToRentRatio,
  pastThreeMonthsAvgTotal,
  totalSelfAttestedCurrentIncome,
  netIncomeToRentRatio,
  criteriaSettings,
  recommendationMethod,
  totalDebtToNetIncome,
  highestCreditScore,
  lowestCreditScore,
  noHitCount
}: {
  highestCreditScore: number;
  lowestCreditScore: number;
  noHitCount: number;
  criteriaTypeOptions: CriteriaTypeOptions;

  totalVerifiedNet?: number;
  creditScoreAvg?: number;
  selfAttestedIncomeToRentRatio?: number;
  pastThreeMonthsAvgTotal?: number;
  totalSelfAttestedCurrentIncome?: number;
  netIncomeToRentRatio?: number;
  totalDebtToNetIncome?: number;
  criteriaSettings?: ApplicantSettings;
  recommendationMethod?: RecommendationMethod;
}) => {
  if (
    !selfAttestedIncomeToRentRatio &&
    !pastThreeMonthsAvgTotal &&
    !totalSelfAttestedCurrentIncome &&
    !netIncomeToRentRatio &&
    !totalDebtToNetIncome &&
    // Need to check this Yevhenii + Yunes
    !totalVerifiedNet &&
    !creditScoreAvg &&
    (!creditScoreAvg || creditScoreAvg == 4)
  )
    return null;

  return (
    <Card className="w-full">
      {!selfAttestedIncomeToRentRatio &&
      !pastThreeMonthsAvgTotal &&
      !totalSelfAttestedCurrentIncome &&
      !netIncomeToRentRatio &&
      !totalDebtToNetIncome &&
      (creditScoreAvg || noHitCount) ? (
        <div className="min-h-[217px]">
          <div className="flex">
            <Stat
              label="Average credit score"
              isCreditScore
              hasCriteria={
                criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
                !!criteriaSettings?.credit_score?.value &&
                !!creditScoreAvg &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (creditScoreAvg || 0) >=
                Number(criteriaSettings?.credit_score?.value || 0)
              }
              criteriaImportance={criteriaSettings?.credit_score?.importance}
              value={creditScoreAvg == 4 ? undefined : creditScoreAvg}
            />
            <Stat
              label="Lowest credit score"
              isCreditScore
              hasCriteria={
                criteriaTypeOptions === CriteriaTypeOptions.MINIMUM &&
                !!criteriaSettings?.credit_score?.value &&
                !!lowestCreditScore &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (lowestCreditScore || 0) >=
                Number(criteriaSettings?.credit_score?.value || 0)
              }
              criteriaImportance={criteriaSettings?.credit_score?.importance}
              value={lowestCreditScore}
            />

            <Stat
              label="Highest credit score"
              isCreditScore
              hasCriteria={
                criteriaTypeOptions === CriteriaTypeOptions.MAXIMUM &&
                !!criteriaSettings?.credit_score?.value &&
                !!highestCreditScore &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (highestCreditScore || 0) >=
                Number(criteriaSettings?.credit_score?.value || 0)
              }
              criteriaImportance={criteriaSettings?.credit_score?.importance}
              value={highestCreditScore}
            />

            <Stat
              label="People with no credit score"
              isCreditScore
              hasCriteria={false}
              criteriaSuccess={false}
              criteriaImportance={undefined}
              value={noHitCount}
            />
          </div>
          <div className="px-6 mt-auto pb-4">
            <CreditScoreGraph
              score={creditScoreAvg == 4 ? 0 : creditScoreAvg || 0}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap md:flex-nowrap h-full">
          <div className="flex flex-col border-r flex-grow border-alpha-40">
            <Stat
              selfReported
              label="Self-reported gross income"
              value={
                totalSelfAttestedCurrentIncome !== undefined
                  ? totalSelfAttestedCurrentIncome / 100
                  : undefined
              }
            />
            <div className="h-px bg-alpha-40" />
            <Stat
              label="Self-reported gross income to rent"
              isRentRatio
              selfReported
              value={selfAttestedIncomeToRentRatio}
            />

            <div className="h-px bg-alpha-40" />
          </div>
          <div className="flex flex-col border-r flex-grow border-alpha-40">
            <Stat
              label="Verified gross income"
              value={pastThreeMonthsAvgTotal}
              hasCriteria={
                !!criteriaSettings?.gross_income &&
                !!pastThreeMonthsAvgTotal &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (pastThreeMonthsAvgTotal || 0) >=
                Number(criteriaSettings?.gross_income?.value || 0)
              }
              criteriaImportance={criteriaSettings?.gross_income?.importance}
            />
            <div className="h-px bg-alpha-40" />
            <Stat
              label="Verified gross income to rent"
              isRentRatio
              value={netIncomeToRentRatio}
              hasCriteria={
                !!criteriaSettings?.gross_income_to_rent_ratio &&
                !!netIncomeToRentRatio &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (netIncomeToRentRatio || 0) >=
                Number(criteriaSettings?.gross_income_to_rent_ratio?.value || 0)
              }
              criteriaImportance={
                criteriaSettings?.gross_income_to_rent_ratio?.importance
              }
            />
          </div>
          <div className="flex flex-col flex-grow border-r border-alpha-40">
            <Stat
              label="Verified net income"
              value={totalVerifiedNet}
              hasCriteria={
                !!criteriaSettings?.net_income &&
                !!totalVerifiedNet &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (totalVerifiedNet || 0) >=
                Number(criteriaSettings?.net_income?.value || 0)
              }
              criteriaImportance={criteriaSettings?.net_income?.importance}
            />
            <div className="h-px bg-alpha-40" />

            <Stat
              label="Average credit score"
              isCreditScore
              hasCriteria={
                criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
                !!criteriaSettings?.credit_score &&
                !!creditScoreAvg &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (creditScoreAvg || 0) >=
                  Number(criteriaSettings?.credit_score?.value || 0) &&
                recommendationMethod === "score_based"
              }
              criteriaImportance={criteriaSettings?.credit_score?.importance}
              value={creditScoreAvg == 4 ? undefined : creditScoreAvg}
            />
            <div className="h-px bg-alpha-40" />
          </div>
          <div className="flex flex-col flex-grow border-r border-alpha-40">
            <Stat
              label="Debt to net income"
              value={totalDebtToNetIncome}
              isPercentage
              hasCriteria={
                !!criteriaSettings?.debt_to_net_income &&
                !!totalDebtToNetIncome &&
                recommendationMethod === "score_based"
              }
              criteriaSuccess={
                (totalDebtToNetIncome || 0) <=
                Number(
                  String(criteriaSettings?.debt_to_net_income?.value).replace(
                    "%",
                    ""
                  ) || 0
                )
              }
              criteriaImportance={
                criteriaSettings?.debt_to_net_income?.importance
              }
            />
            <div className="h-px bg-alpha-40" />
            <Stat
              label="People with no credit score"
              isCreditScore
              hasCriteria={false}
              criteriaSuccess={false}
              criteriaImportance={undefined}
              value={noHitCount}
            />
            <div className="h-px bg-alpha-40" />
          </div>

          <div className="flex flex-col flex-grow">
            <Stat
              label="Highest credit score"
              isCreditScore
              hasCriteria={false}
              criteriaSuccess={false}
              criteriaImportance={undefined}
              value={highestCreditScore}
            />
            <div className="h-px bg-alpha-40" />
            <Stat
              label="Lowest credit score"
              isCreditScore
              hasCriteria={false}
              criteriaSuccess={false}
              criteriaImportance={undefined}
              value={lowestCreditScore}
            />
            <div className="h-px bg-alpha-40" />
          </div>
        </div>
      )}
    </Card>
  );
};
